import React from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { FieldConfig } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { transformProjectType } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-project-type/index.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import messages from './messages';

type Props = {
	isDisabled?: boolean;
	fieldConfig?: FieldConfig;
};

const testId = 'issue-create.ui.modal.create-form.actions-container.dropdown-menu.dropdown-menu';
const buttonTestId = 'issue-create.ui.modal.create-form.actions-container.dropdown-menu.button';

export const HeaderDropdown = (props: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const isAdmin = useIsAdmin();
	const { isDisabled = false } = props;

	const [
		{ selectedProject, selectedIssueType },
		{ toggleConfigureFieldsModalVisibility, toggleFindFieldsModalVisibility },
	] = useFormContext();

	const showConfigureFieldsModal = (): void => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		toggleConfigureFieldsModalVisibility(true);
		fireUIAnalytics(analyticsEvent, 'configureFields', {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
		});
	};

	const showFindFieldModal = (): void => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		toggleFindFieldsModalVisibility(true);
		fireUIAnalytics(analyticsEvent, 'findYourField', {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
		});
	};

	const showFindFieldOption = (): boolean =>
		Boolean(isAdmin && selectedProject && selectedIssueType && !selectedProject.isSimplified);

	// @ts-expect-error - TS7006 - Parameter 'attributes' implicitly has an 'any' type.
	const onOpenChange = (attributes) => {
		if (attributes.isOpen) {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});
			fireUIAnalytics(analyticsEvent, 'meatballMenu');
		}
	};

	return (
		<DropdownMenu
			placement="bottom-end"
			shouldRenderToParent
			trigger={({ triggerRef, ...dropdownProps }) => (
				<Button
					{...dropdownProps}
					testId={buttonTestId}
					iconBefore={<MoreIcon label="" />}
					appearance="subtle"
					isDisabled={isDisabled}
					ref={triggerRef}
					aria-label={formatMessage(
						fg('jira-issue-terminology-refresh')
							? messages.moreActionsIssuetermrefresh
							: messages.moreActions,
					)}
				/>
			)}
			onOpenChange={onOpenChange}
			testId={testId}
		>
			<DropdownItemGroup>
				<DropdownItem onClick={showConfigureFieldsModal}>
					{formatMessage(messages.showFields)}
				</DropdownItem>
				{showFindFieldOption() && (
					<DropdownItem onClick={showFindFieldModal}>
						{formatMessage(messages.findField)}
					</DropdownItem>
				)}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
